import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import logo from '../../assets/images/M_Multicolor.svg';
import MomentumClawLanding from './MomentumClawLanding';
import MomentumClawChat from './MomentumClawChat';
import { checkGatewayStatus, startGateway, checkOpenClawConfig, requestOpenClawAccess, getMomentumClawSSOToken } from '../../services/momentumClawService';
import '../../styles/MomentumClaw.css';
function generateGuid(): string {
    if (crypto.randomUUID)
        return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
}
type GatewayState = 'idle' | 'checking' | 'starting' | 'ready' | 'error';
const GATEWAY_POLL_INTERVAL_MS = 3000;
const GATEWAY_MAX_RETRIES = 20;
const MomentumClawPage: React.FC = () => {
    const { conversationId } = useParams<{
        conversationId?: string;
    }>();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [configChecked, setConfigChecked] = useState(false);
    const [configuredReady, setConfiguredReady] = useState(false);
    const [launching, setLaunching] = useState(false);
    const [launchError, setLaunchError] = useState(false);
    const [requesting, setRequesting] = useState(false);
    const [requested, setRequested] = useState(false);
    const [gatewayState, setGatewayState] = useState<GatewayState>('idle');
    const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const retriesRef = useRef(0);
    const isChat = !!conversationId;
    useEffect(() => {
        if (isChat)
            return;
        let cancelled = false;
        async function check() {
            setLoading(true);
            const configured = await checkOpenClawConfig();
            if (cancelled)
                return;
            if (configured) {
                setConfiguredReady(true);
                setLoading(false);
            }
            else {
                setLoading(false);
                setConfigChecked(true);
            }
        }
        check();
        return () => { cancelled = true; };
    }, [isChat, navigate]);
    const handleRequestAccess = async () => {
        setRequesting(true);
        await requestOpenClawAccess();
        setRequesting(false);
        setRequested(true);
    };
    useEffect(() => {
        if (!conversationId) {
            setLoading(true);
            setConfigChecked(false);
            setConfiguredReady(false);
            setRequested(false);
            setRequesting(false);
        }
    }, [conversationId]);
    useEffect(() => {
        if (!isChat) {
            setGatewayState('idle');
            retriesRef.current = 0;
            if (pollRef.current)
                clearTimeout(pollRef.current);
            return;
        }
        let cancelled = false;
        async function pollUntilRunning() {
            const status = await checkGatewayStatus();
            if (cancelled)
                return;
            if (status === 'running') {
                setGatewayState('ready');
                return;
            }
            if (retriesRef.current >= GATEWAY_MAX_RETRIES) {
                setGatewayState('error');
                return;
            }
            retriesRef.current += 1;
            pollRef.current = setTimeout(pollUntilRunning, GATEWAY_POLL_INTERVAL_MS);
        }
        async function initGateway() {
            setGatewayState('checking');
            retriesRef.current = 0;
            const status = await checkGatewayStatus();
            if (cancelled)
                return;
            if (status === 'running') {
                setGatewayState('ready');
                return;
            }
            setGatewayState('starting');
            try {
                await startGateway();
            }
            catch {
            }
            if (cancelled)
                return;
            pollRef.current = setTimeout(pollUntilRunning, GATEWAY_POLL_INTERVAL_MS);
        }
        initGateway();
        return () => {
            cancelled = true;
            if (pollRef.current)
                clearTimeout(pollRef.current);
        };
    }, [isChat]);
    const gatewayLoading = isChat && (gatewayState === 'checking' || gatewayState === 'starting');
    const gatewayLabel = gatewayState === 'checking' ? 'Verifying gateway status…' :
        gatewayState === 'starting' ? 'Starting OpenClaw gateway…' : '';
    return (<div className="mc-page">
      
      {!isChat && configuredReady && (<motion.div className="mc-loader" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
          <div className="mc-loader__card">
            <div className="mc-loader__glow"/>
            <motion.div className="mc-loader__logo-wrap" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }}>
              <div className="mc-loader__ring mc-loader__ring--outer"/>
              <div className="mc-loader__ring mc-loader__ring--inner"/>
              <img src={logo} alt="MomentumClaw" className="mc-loader__logo"/>
            </motion.div>
            <motion.div className="mc-loader__text" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.35 }}>
              <span className="mc-loader__title">
                {launching ? 'Launching MomentumClaw…' : launchError ? 'Launch Failed' : 'MomentumClaw is Ready'}
              </span>
              <span className="mc-loader__subtitle">
                {launching ? 'Generating secure session token…' : launchError ? 'Could not generate session. Please try again.' : 'Your workspace is configured and ready to use.'}
              </span>
            </motion.div>

            {launching ? (<motion.div className="mc-loader__dots" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
                <span /><span /><span />
              </motion.div>) : (<motion.button className="mc-launch-btn" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.3 }} disabled={launching} onClick={async () => {
                    setLaunching(true);
                    setLaunchError(false);
                    const ssoToken = await getMomentumClawSSOToken();
                    setLaunching(false);
                    if (!ssoToken) {
                        setLaunchError(true);
                        return;
                    }
                    window.open(`https://momentumclaw.app/auth/momentum?token=${ssoToken}`, '_blank');
                }}>
                {launchError ? 'Retry Launch' : 'Click here to launch MomentumClaw'}
              </motion.button>)}
          </div>
        </motion.div>)}

      
      {!isChat && configChecked && !loading && (<MomentumClawLanding onRequestAccess={handleRequestAccess} requested={requested} requesting={requesting} checking={false}/>)}

      {(!isChat && loading) || gatewayLoading ? (<motion.div className="mc-loader" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
          <div className="mc-loader__card">
            <div className="mc-loader__glow"/>

            <motion.div className="mc-loader__logo-wrap" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }}>
              <div className="mc-loader__ring mc-loader__ring--outer"/>
              <div className="mc-loader__ring mc-loader__ring--inner"/>
              <img src={logo} alt="MomentumClaw" className="mc-loader__logo"/>
            </motion.div>

            <motion.div className="mc-loader__text" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.35 }}>
              <span className="mc-loader__title">Connecting to MomentumClaw</span>
              <span className="mc-loader__subtitle">{gatewayLoading ? gatewayLabel : 'Setting up your session…'}</span>
            </motion.div>

            <motion.div className="mc-loader__dots" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
              <span /><span /><span />
            </motion.div>
          </div>
        </motion.div>) : null}

      {isChat && gatewayState === 'error' && (<motion.div className="mc-loader" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
          <div className="mc-loader__card">
            <div className="mc-loader__glow"/>
            <motion.div className="mc-loader__text" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.35 }}>
              <span style={{ fontSize: 28 }}>⚠️</span>
              <span className="mc-loader__title">Gateway Unavailable</span>
              <span className="mc-loader__subtitle">Could not start the OpenClaw gateway. Please try again later.</span>
            </motion.div>
          </div>
        </motion.div>)}

      {isChat && gatewayState === 'ready' && (<MomentumClawChat key={conversationId} conversationId={conversationId}/>)}
    </div>);
};
export default MomentumClawPage;
